import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { Search, ShieldAlert, History, UserMinus } from "lucide-react";
import { toast } from "sonner";

const ENTITY_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "site", label: "Site" },
  { value: "guard", label: "Officer" },
  { value: "shift", label: "Shift" },
  { value: "shift_series", label: "Shift Series" },
  { value: "incident", label: "Incident" },
  { value: "user", label: "User" },
];
const ACTION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "invite", label: "Invite" },
];
const TONE_FOR = { create: "green", update: "blue", delete: "red", invite: "amber" };

const PAGE_SIZE = 50;

export default function AuditTrail() {
  const [tab, setTab] = useState("changes"); // "changes" | "violations" | "coverage"
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState([]);
  const [actionFilter, setActionFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (entityFilter.length === 1) params.entity_type = entityFilter[0];
      if (actionFilter.length === 1) params.action = actionFilter[0];
      const res = await api.get("/audit", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load audit log"));
    }
    setLoading(false);
  }, [offset, entityFilter, actionFilter]);

  useEffect(() => { load(); }, [load]);

  // Frontend-only fuzzy search across loaded page
  const q = search.trim().toLowerCase();
  const visible = q ? items.filter((i) =>
    (i.summary || "").toLowerCase().includes(q) ||
    (i.user_email || "").toLowerCase().includes(q) ||
    (i.entity_id || "").toLowerCase().includes(q)
  ) : items;
  // Multi-select fallback when more than one chosen
  const filteredByFacet = visible.filter((i) =>
    (entityFilter.length <= 1 || entityFilter.includes(i.entity_type)) &&
    (actionFilter.length <= 1 || actionFilter.includes(i.action))
  );

  return (
    <div>
      <PageHeader
        title="Audit Trail"
        subtitle="Every create / update / delete plus every access-policy violation (401/403). Admin scope only."
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-1 border-b border-[#27272A]" data-testid="audit-tabs">
          <button
            data-testid="audit-tab-changes"
            onClick={() => setTab("changes")}
            className={`px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px ${tab === "changes" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            <History className="size-3.5" /> Change Log
          </button>
          <button
            data-testid="audit-tab-violations"
            onClick={() => setTab("violations")}
            className={`px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px ${tab === "violations" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            <ShieldAlert className="size-3.5" /> Access Violations
          </button>
          <button
            data-testid="audit-tab-coverage"
            onClick={() => setTab("coverage")}
            className={`px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px ${tab === "coverage" ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            <UserMinus className="size-3.5" /> Coverage Changes
          </button>
        </div>

        {tab === "violations" && <AccessViolationsPanel />}
        {tab === "coverage" && <CoverageChangesPanel />}
        {tab === "changes" && (
          <>
        <div className="flex flex-wrap items-end gap-3" data-testid="audit-filters">
          <div className="flex-1 min-w-[240px] max-w-sm">
            <div className="label-mute mb-1">Search</div>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="audit-search" className={`${inputClass} pl-10`}
                placeholder="Search summary, user, entity id…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div>
            <div className="label-mute mb-1">Entity</div>
            <MultiSelectDropdown testId="audit-entity-filter" label="All entities" options={ENTITY_OPTIONS}
              selected={entityFilter} onChange={(v) => { setEntityFilter(v); setOffset(0); }} triggerClassName="min-w-[170px]" />
          </div>
          <div>
            <div className="label-mute mb-1">Action</div>
            <MultiSelectDropdown testId="audit-action-filter" label="All actions" options={ACTION_OPTIONS}
              selected={actionFilter} onChange={(v) => { setActionFilter(v); setOffset(0); }} triggerClassName="min-w-[150px]" />
          </div>
          <div className="ml-auto text-xs font-mono text-zinc-500 pb-2" data-testid="audit-pagination">
            {offset + 1}–{Math.min(offset + items.length, total)} of {total}
          </div>
          <div className="inline-flex gap-1 pb-1">
            <GhostButton data-testid="audit-prev" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>← Prev</GhostButton>
            <GhostButton data-testid="audit-next" disabled={offset + items.length >= total} onClick={() => setOffset((o) => o + PAGE_SIZE)}>Next →</GhostButton>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-4 py-3 text-left"><div className="label-mute">When</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Action</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Entity</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Summary</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">User</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Diff</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={6} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && filteredByFacet.length === 0 && (
                <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">No audit events match the filters.</td></tr>
              )}
              {filteredByFacet.map((i) => {
                const diffKeys = Object.keys(i.diff || {});
                const isOpen = expanded === i.id;
                return (
                  <tr key={i.id} className="hover:bg-[#1c1c1e] align-top" data-testid={`audit-row-${i.id}`}>
                    <td className="px-4 py-3 font-mono text-zinc-400 text-xs whitespace-nowrap">
                      {new Date(i.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3"><PillBadge tone={TONE_FOR[i.action] || "default"}>{i.action}</PillBadge></td>
                    <td className="px-4 py-3 text-zinc-300 font-mono text-xs">{i.entity_type} <span className="text-zinc-600">·</span> {(i.entity_id || "").slice(0, 8)}</td>
                    <td className="px-4 py-3 text-zinc-300">{i.summary || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{i.user_email || "—"}<div className="text-[10px] uppercase tracking-widest text-zinc-600">{i.user_role}</div></td>
                    <td className="px-4 py-3 text-right">
                      {diffKeys.length > 0 ? (
                        <button data-testid={`audit-toggle-${i.id}`} onClick={() => setExpanded(isOpen ? null : i.id)}
                          className="text-xs uppercase tracking-widest text-[#0A84FF] hover:underline">
                          {isOpen ? "Hide" : `${diffKeys.length} change${diffKeys.length === 1 ? "" : "s"}`}
                        </button>
                      ) : <span className="text-zinc-600 text-xs">—</span>}
                      {isOpen && (
                        <pre className="mt-2 text-left text-[10px] text-zinc-400 bg-[#0A0A0A] border border-[#27272A] p-2 max-w-[420px] overflow-x-auto font-mono whitespace-pre-wrap" data-testid={`audit-diff-${i.id}`}>
{Object.entries(i.diff || {}).map(([k, v]) => `${k}: ${JSON.stringify(v[0])} → ${JSON.stringify(v[1])}`).join("\n")}
                        </pre>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Access Violations Panel — 401/403 attempts logged by the backend
// access_violation_logger middleware.
// ─────────────────────────────────────────────────────────────────────
const VIOL_TONE = { 401: "amber", 403: "red" };
const ROLE_OPTIONS = [
  { value: "supervisor", label: "Supervisor" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "operations_manager", label: "Operations Manager" },
  { value: "chief_security_officer", label: "Chief Security Officer" },
  { value: "guard", label: "Officer" },
  { value: "client", label: "Client" },
];

function AccessViolationsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [pathQ, setPathQ] = useState("");
  const [days, setDays] = useState(30);
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  const params = useMemo(() => {
    const p = { limit: PAGE, offset, days };
    if (roleFilter.length === 1) p.role = roleFilter[0];
    if (statusFilter.length === 1) p.status_code = statusFilter[0];
    if (pathQ.trim()) p.path = pathQ.trim();
    return p;
  }, [offset, days, roleFilter, statusFilter, pathQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audit/access-violations", { params });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load access violations"));
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const byRole = useMemo(() => {
    const m = {};
    rows.forEach((r) => { const k = r.role || "(anon)"; m[k] = (m[k] || 0) + 1; });
    return m;
  }, [rows]);
  const byPath = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.path] = (m[r.path] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="access-violations-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
          <div className="label-mute">Violations (in window)</div>
          <div className="font-display text-2xl font-black text-[#FF3B30]" data-testid="violations-total">{total}</div>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3 col-span-1 md:col-span-3">
          <div className="label-mute mb-2">Top offenders (path)</div>
          {byPath.length === 0 ? (
            <div className="text-zinc-500 text-xs italic">No violations in this window.</div>
          ) : (
            <div className="space-y-1">
              {byPath.map(([p, n]) => (
                <div key={p} className="flex items-center justify-between text-xs font-mono">
                  <span className="truncate">{p}</span>
                  <span className="text-[#FF3B30] font-bold">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px] max-w-sm">
          <div className="label-mute mb-1">Path starts with</div>
          <input data-testid="violations-path" className={inputClass}
            placeholder="/api/users" value={pathQ} onChange={(e) => { setPathQ(e.target.value); setOffset(0); }} />
        </div>
        <div>
          <div className="label-mute mb-1">Role</div>
          <MultiSelectDropdown testId="violations-role-filter" label="All roles" options={ROLE_OPTIONS}
            selected={roleFilter} onChange={(v) => { setRoleFilter(v); setOffset(0); }} triggerClassName="min-w-[170px]" />
        </div>
        <div>
          <div className="label-mute mb-1">Status</div>
          <MultiSelectDropdown testId="violations-status-filter" label="All statuses"
            options={[{ value: 401, label: "401 · Not Authenticated" }, { value: 403, label: "403 · Forbidden" }]}
            selected={statusFilter} onChange={(v) => { setStatusFilter(v); setOffset(0); }} triggerClassName="min-w-[200px]" />
        </div>
        <div>
          <div className="label-mute mb-1">Window (days)</div>
          <select data-testid="violations-days" className={inputClass} value={days} onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }}>
            {[1, 7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="ml-auto text-xs font-mono text-zinc-500 pb-2">
          {rows.length === 0 ? "0" : `${offset + 1}–${Math.min(offset + rows.length, total)} of ${total}`}
        </div>
        <div className="inline-flex gap-1 pb-1">
          <GhostButton data-testid="violations-prev" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>← Prev</GhostButton>
          <GhostButton data-testid="violations-next" disabled={offset + rows.length >= total} onClick={() => setOffset((o) => o + PAGE)}>Next →</GhostButton>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm" data-testid="violations-table">
          <thead className="border-b border-[#27272A]">
            <tr>
              <th className="px-4 py-3 text-left"><div className="label-mute">When</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Status</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Method · Path</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">User</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Role</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">IP</div></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#27272A]">
            {loading && <tr><td colSpan={6} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">Nothing blocked in this window. 👍</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#1c1c1e]" data-testid={`violation-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-400 text-xs whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3"><PillBadge tone={VIOL_TONE[r.status_code] || "default"}>{r.status_code}</PillBadge></td>
                <td className="px-4 py-3 text-zinc-300 font-mono text-xs">
                  <span className="text-zinc-500">{r.method}</span> {r.path}
                </td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{r.user_email || <span className="text-zinc-600 italic">(anonymous)</span>}</td>
                <td className="px-4 py-3 text-xs">
                  {r.role ? <PillBadge tone="default">{r.role.replace(/_/g, " ")}</PillBadge> : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{r.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// Coverage Changes Panel — supervisor unassignments from `released_assignments`
// where role="supervisor". Sourced from GET /api/audit/coverage-changes.
// ─────────────────────────────────────────────────────────────────────
function CoverageChangesPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState([]);
  const [days, setDays] = useState(90);
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  // Load sites list once for filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/sites");
        const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
        setSites(list.map((s) => ({ value: s.id, label: s.name })));
      } catch (err) {
        // Non-fatal — filter just remains empty. Log in dev for support triage.
        if (process.env.NODE_ENV === "development") console.debug("AuditTrail: /sites fetch failed:", err?.response?.status);
      }
    })();
  }, []);

  const params = useMemo(() => {
    const p = { limit: PAGE, offset, days };
    if (siteFilter.length === 1) p.site_id = siteFilter[0];
    return p;
  }, [offset, days, siteFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audit/coverage-changes", { params });
      let items = r.data.items || [];
      // Client-side multi-site fallback
      if (siteFilter.length > 1) items = items.filter((i) => siteFilter.includes(i.site_id));
      setRows(items);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load coverage changes"));
    } finally { setLoading(false); }
  }, [params, siteFilter]);

  useEffect(() => { load(); }, [load]);

  const bySite = useMemo(() => {
    const m = {};
    rows.forEach((r) => { const k = r.site_name || "(unknown)"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="coverage-changes-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
          <div className="label-mute">Supervisor releases (in window)</div>
          <div className="font-display text-2xl font-black text-[#FF9F0A]" data-testid="coverage-total">{total}</div>
        </div>
        <div className="border border-[#27272A] bg-[#0A0A0A] p-3 col-span-1 md:col-span-3">
          <div className="label-mute mb-2">Top sites (releases)</div>
          {bySite.length === 0 ? (
            <div className="text-zinc-500 text-xs italic">No coverage changes in this window.</div>
          ) : (
            <div className="space-y-1">
              {bySite.map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-xs font-mono">
                  <span className="truncate">{s}</span>
                  <span className="text-[#FF9F0A] font-bold">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="label-mute mb-1">Site</div>
          <MultiSelectDropdown testId="coverage-site-filter" label="All sites" options={sites}
            selected={siteFilter} onChange={(v) => { setSiteFilter(v); setOffset(0); }} triggerClassName="min-w-[220px]" />
        </div>
        <div>
          <div className="label-mute mb-1">Window (days)</div>
          <select data-testid="coverage-days" className={inputClass} value={days} onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }}>
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="ml-auto text-xs font-mono text-zinc-500 pb-2">
          {rows.length === 0 ? "0" : `${offset + 1}–${Math.min(offset + rows.length, total)} of ${total}`}
        </div>
        <div className="inline-flex gap-1 pb-1">
          <GhostButton data-testid="coverage-prev" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>← Prev</GhostButton>
          <GhostButton data-testid="coverage-next" disabled={offset + rows.length >= total} onClick={() => setOffset((o) => o + PAGE)}>Next →</GhostButton>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm" data-testid="coverage-table">
          <thead className="border-b border-[#27272A]">
            <tr>
              <th className="px-4 py-3 text-left"><div className="label-mute">When</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Supervisor</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Site</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Client</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Released by</div></th>
              <th className="px-4 py-3 text-left"><div className="label-mute">Reason</div></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#27272A]">
            {loading && <tr><td colSpan={6} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-16 text-zinc-500 scanlines">No supervisor removals in this window.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#1c1c1e]" data-testid={`coverage-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-400 text-xs whitespace-nowrap">
                  {r.released_at ? new Date(r.released_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-zinc-200 text-xs">{r.guard_name || "—"}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{r.site_name || <span className="text-zinc-600">—</span>}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{r.client_name || <span className="text-zinc-600">—</span>}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{r.released_by_name || <span className="text-zinc-600">—</span>}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs italic">{r.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
